import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';
import * as simulator from './simulator';

export default class MQTTPubNode extends BaseNode {
    constructor() {
        super();
        this.name = 'MQTT Publish';
        this.id = 'node_mqtt_pub';
        this.icon = 'AiOutlineCloudUpload';
        this.linkedFeature = 'mqtt';
        this.requirements = ['network'];

        this.inputs = [
            {
                name: 'Publish',
                portId: 'mqtt_publish',
                params: [
                    {
                        name: 'topic',
                        dataType: TibboDataType.STRING,
                        default: '',
                    },
                    {
                        name: 'publish_data',
                        dataType: TibboDataType.STRING,
                        default: '',
                    },
                    {
                        name: 'qos',
                        dataType: TibboDataType.CUSTOM,
                        default: 'QOS_AT_MOST_ONCE',
                    },
                    {
                        name: 'retain',
                        dataType: TibboDataType.CUSTOM,
                        default: 'false',
                    },
                ],
            },
        ];
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        switch (settingId) {
            case 'qos':
                return [
                    {
                        name: 'QOS_AT_MOST_ONCE',
                        value: 'QOS_AT_MOST_ONCE',
                    },
                    {
                        name: 'QOS_AT_LEAST_ONCE',
                        value: 'QOS_AT_LEAST_ONCE',
                    },
                    {
                        name: 'QOS_EXACTLY_ONCE',
                        value: 'QOS_EXACTLY_ONCE',
                    },
                ];
            case 'retain':
                return [
                    {
                        name: 'true',
                        value: 'true',
                    },
                    {
                        name: 'false',
                        value: 'false',
                    },
                ];
            default:
                return super.getSettingOptions(settingId);
        }
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const project: ProjectConfiguration = this.project;
            const links = project.getNodeLinks(this, 'mqtt_publish');
            if (links.length > 0) {
                const link = links[0];
                const topic = project.getNodeParameterValue(this, 'topic');
                return `MQTT publish to ${topic}`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const links = project.getNodeLinks(this, 'mqtt_publish');
        if (links.length > 0) {
            const link = links[0];
            const topic = project.getNodeParameterValue(this, 'topic');
            return `MQTT publish to ${topic}`;
        }
        return this.name;
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/taiko/mqtt_library';
    }
}
